import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import Loader from "components/Loader";
import client from "libs/client";
import type { CompetitionEditionRowResponse } from "libs/apiClient";
import { getApiErrorMessage } from "libs/utils";
import { isGranted } from "pages/productManagement/functions";
import { Permission } from "pages/productManagement/types";
import { useToast } from "ui-kit";
import { CompetitionEditionCreateSeasonModal } from "./CompetitionEditionCreateSeasonModal";
import { CompetitionEditionFormModal } from "./CompetitionEditionFormModal";

export type CompetitionEditionsPanelProps = {
	competitionId: number;
	competitionName: string;
	hasGroups?: boolean;
	hasPhases?: boolean;
	seasonFilterId?: number;
};

function extractMutationErrorMessage(error: unknown): string {
	if (error && typeof error === "object") {
		const err = error as {
			json?: string | { message?: string };
			message?: string;
		};
		if (typeof err.json === "string") {
			return err.json;
		}
		if (err.json?.message) {
			return err.json.message;
		}
		if (err.message) {
			return err.message;
		}
	}
	return "Something went wrong";
}

export function CompetitionEditionsPanel({
	competitionId,
	competitionName,
	hasGroups = false,
	hasPhases = false,
	seasonFilterId,
}: CompetitionEditionsPanelProps) {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { addSuccessToast, addErrorToast } = useToast();
	const canCreate = isGranted("competition", Permission.CREATE);
	const canEdit = isGranted("competition", Permission.EDIT);

	const [showCreateModal, setShowCreateModal] = useState(false);
	const [showCreateSeasonModal, setShowCreateSeasonModal] = useState(false);
	const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
	const [bulkProgress, setBulkProgress] = useState<{ completed: number; total: number } | null>(
		null,
	);

	const { data: editionsResponse, isLoading, isError, refetch } = client.getCompetitionEditions.useQuery(
		{ id: competitionId },
		{ retry: false },
	);

	const patchVisibilityMutation = client.patchCompetitionEditionScoutVisibility.useMutation();

	const editions = useMemo(() => {
		const items = editionsResponse?.data?.items ?? [];
		if (seasonFilterId == null || Number.isFinite(seasonFilterId) === false) {
			return items;
		}
		return items.filter((edition) => edition.seasonId === seasonFilterId);
	}, [editionsResponse, seasonFilterId]);

	const invalidateEditions = useCallback(async () => {
		await queryClient.invalidateQueries({ queryKey: ["getCompetitionEditions", { id: competitionId }] });
		await queryClient.invalidateQueries({ queryKey: ["getCompetitions"] });
	}, [competitionId, queryClient]);

	const handleToggleSelection = useCallback((editionId: number) => {
		setSelectedIds((previous) => {
			const next = new Set(previous);
			if (next.has(editionId)) {
				next.delete(editionId);
			} else {
				next.add(editionId);
			}
			return next;
		});
	}, []);

	const handleClearSelection = useCallback(() => {
		setSelectedIds(new Set());
	}, []);

	const selectedEditions = useMemo((): CompetitionEditionRowResponse[] => {
		return editions.filter(
			(edition) => edition.id != null && selectedIds.has(edition.id),
		);
	}, [editions, selectedIds]);

	const canCreateNewSeason = useMemo(() => {
		if (selectedEditions.length === 0) {
			return false;
		}

		const firstSeasonId = selectedEditions[0]?.seasonId;
		if (firstSeasonId == null) {
			return false;
		}

		return selectedEditions.every((edition) => edition.seasonId === firstSeasonId);
	}, [selectedEditions]);

	const editionsSelectionKey = editions
		.map((edition) => edition.id ?? 0)
		.join(",");

	useEffect(() => {
		setSelectedIds(new Set());
	}, [seasonFilterId, editionsSelectionKey]);

	const handleBulkVisibility = useCallback(
		async (isScoutVisible: boolean) => {
			if (canEdit === false || selectedIds.size === 0 || bulkProgress != null) {
				return;
			}

			const editionIds = Array.from(selectedIds);
			setBulkProgress({ completed: 0, total: editionIds.length });
			try {
				let completedCount = 0;
				for (const editionId of editionIds) {
					await patchVisibilityMutation.mutateAsync({
						id: editionId,
						body: { isScoutVisible },
					});
					completedCount += 1;
					setBulkProgress({ completed: completedCount, total: editionIds.length });
				}
				addSuccessToast(
					isScoutVisible
						? `Showed ${completedCount} edition${completedCount === 1 ? "" : "s"}`
						: `Hid ${completedCount} edition${completedCount === 1 ? "" : "s"}`,
				);
				setSelectedIds(new Set());
				await invalidateEditions();
			} catch (error: unknown) {
				addErrorToast(getApiErrorMessage(extractMutationErrorMessage(error)));
			} finally {
				setBulkProgress(null);
			}
		},
		[
			addErrorToast,
			addSuccessToast,
			bulkProgress,
			canEdit,
			invalidateEditions,
			patchVisibilityMutation,
			selectedIds,
		],
	);

	if (isLoading) {
		return (
			<div className="competitions-editions-panel">
				<Loader />
			</div>
		);
	}

	if (isError) {
		return (
			<div className="competitions-editions-panel">
				<p>Failed to load editions.</p>
				<button type="button" className="btn btn-light-blue" onClick={() => refetch()}>
					Retry
				</button>
			</div>
		);
	}

	const selectedCount = selectedIds.size;
	const hasSelection = selectedCount > 0;
	const isBulkUpdating = bulkProgress != null;

	return (
		<div className="competitions-editions-panel">
			<div className="competitions-editions-panel__toolbar">
				<div className="competitions-editions-panel__toolbarLeft">
					{hasSelection ? (
						<>
							<span className="competitions-editions-panel__selectedCount">
								{selectedCount} selected
							</span>
							{canCreate ? (
								<button
									type="button"
									className="competitions-editions-panel__ghostBtn"
									disabled={isBulkUpdating || canCreateNewSeason === false}
									title={
										canCreateNewSeason
											? "Create copies in a new season"
											: "Select editions from the same season"
									}
									onClick={() => setShowCreateSeasonModal(true)}
								>
									Create new season
								</button>
							) : null}
							{canEdit ? (
								<>
									<button
										type="button"
										className="competitions-editions-panel__ghostBtn"
										disabled={isBulkUpdating}
										onClick={() => handleBulkVisibility(false)}
									>
										Hide selected
									</button>
									<button
										type="button"
										className="competitions-editions-panel__ghostBtn"
										disabled={isBulkUpdating}
										onClick={() => handleBulkVisibility(true)}
									>
										Show selected
									</button>
								</>
							) : null}
							<button
								type="button"
								className="competitions-editions-panel__ghostBtn"
								disabled={isBulkUpdating}
								onClick={handleClearSelection}
							>
								Clear
							</button>
							{bulkProgress != null ? (
								<span className="competitions-editions-panel__bulkProgress">
									Updating {bulkProgress.completed}/{bulkProgress.total}
								</span>
							) : null}
						</>
					) : (
						<strong className="competitions-editions-panel__title">Season editions</strong>
					)}
				</div>
				{canCreate ? (
					<button
						type="button"
						className="btn btn-light-blue btn--sm"
						onClick={() => setShowCreateModal(true)}
					>
						+ Add season edition
					</button>
				) : null}
			</div>

			{editions.length === 0 ? (
				<p className="competitions-editions-panel__empty">No season editions yet.</p>
			) : (
				<ul className="competitions-editions-list">
					{editions.map((edition) => {
						const editionId = edition.id;
						const editionKey =
							editionId ?? `${edition.seasonId}-${edition.groupLabel}-${edition.phaseLabel}`;
						const isVisible = edition.isScoutVisible === true;
						const isSelected = editionId != null && selectedIds.has(editionId);
						const teamCount = edition.teamCount ?? 0;
						const groupLabel =
							edition.groupLabel != null && edition.groupLabel !== ""
								? edition.groupLabel
								: "-";
						const phaseLabel =
							edition.phaseLabel != null && edition.phaseLabel !== ""
								? edition.phaseLabel
								: "-";

						return (
							<li
								key={editionKey}
								className={`competitions-editions-list__row ${isSelected ? "is-selected" : ""}`}
							>
								{editionId != null ? (
									<label className="competitions-editions-list__check">
										<input
											type="checkbox"
											checked={isSelected}
											onChange={() => handleToggleSelection(editionId)}
											aria-label={`Select edition ${editionId}`}
										/>
									</label>
								) : (
									<span className="competitions-editions-list__check" />
								)}

								<div className="competitions-editions-list__season">
									<span className="icon icon-new icon-16 icon-dark-grey icon-calendar" aria-hidden="true" />
									<span>{edition.seasonName ?? "-"}</span>
								</div>

								<div className="competitions-editions-list__meta">
									<span className="competitions-editions-list__metaLabel">Group</span>
									<span>{groupLabel}</span>
								</div>

								<div className="competitions-editions-list__meta">
									<span className="competitions-editions-list__metaLabel">Edition ID</span>
									<span className="competitions-table__mono">
										{editionId != null ? `#${editionId}` : "-"}
									</span>
								</div>

								<div className="competitions-editions-list__meta">
									<span className="competitions-editions-list__metaLabel">Phase</span>
									<span>{phaseLabel}</span>
								</div>

								<div className="competitions-editions-list__badges">
									<span
										className={`competitions-visibility-badge ${
											isVisible ? "is-visible" : "is-hidden"
										}`}
									>
										<span
											className={`icon icon-new icon-eye ${
												isVisible ? "icon-success" : "icon-danger"
											}`}
											aria-hidden="true"
										/>
										{isVisible ? "Visible" : "Hidden"}
									</span>
									<span className="competitions-badge competitions-badge--muted">
										{teamCount} team{teamCount === 1 ? "" : "s"}
									</span>
								</div>

								<button
									type="button"
									className="competitions-editions-list__openBtn"
									disabled={editionId == null}
									onClick={() => {
										if (editionId == null) {
											return;
										}
										navigate(`/product-management/competitions/editions/${editionId}`);
									}}
								>
									Open
									<span
										className="icon icon-new icon-12 icon-dark-grey icon-chevron-right-grey"
										aria-hidden="true"
									/>
								</button>
							</li>
						);
					})}
				</ul>
			)}

			{showCreateModal ? (
				<CompetitionEditionFormModal
					competitionId={competitionId}
					hasGroups={hasGroups}
					hasPhases={hasPhases}
					onClose={() => setShowCreateModal(false)}
					onSaved={invalidateEditions}
				/>
			) : null}

			{showCreateSeasonModal && canCreateNewSeason ? (
				<CompetitionEditionCreateSeasonModal
					competitionName={competitionName}
					editions={selectedEditions}
					onClose={() => setShowCreateSeasonModal(false)}
					onCreated={async () => {
						setSelectedIds(new Set());
						await invalidateEditions();
					}}
				/>
			) : null}
		</div>
	);
}

export default CompetitionEditionsPanel;
